export type HomepageEditorialCopy = {
  indexLabel: string;
  searchDirectory: string;
  pressSlash: string;
  searchLabel: string;
  quickSearches: string;
  categoryRoutes: string;
  latestToolsCount: string;
  updated: string;
  featuredKicker: string;
  featuredTitle: string;
  featuredDescription: string;
  toolsSnapshot: string;
  categoriesTitle: string;
  categoryShortcuts: string;
  allCategories: string;
  latestKicker: string;
  latestTitle: string;
  latestDescription: string;
  browseAllTools: string;
  all: string;
  toolsShown: string;
  noToolsTitle: string;
  noToolsDescription: string;
  clearFilters: string;
  productOverview: string;
  viewDetails: string;
  skillsDescription: string;
  browseSkills: string;
  newsTitle: string;
  newsDescription: string;
  browseNews: string;
  faqDescription: string;
  submitTool: string;
  openNavigation: string;
  closeNavigation: string;
  language: string;
};

const COPY: Record<string, HomepageEditorialCopy> = {
  en: {
    indexLabel: 'AIToolly Index',
    searchDirectory: 'Search the directory',
    pressSlash: 'Press /',
    searchLabel: 'What do you want an AI tool to help with?',
    quickSearches: 'Quick searches',
    categoryRoutes: '{count} category routes',
    latestToolsCount: '{count} recently added tools',
    updated: 'Updated {date}',
    featuredKicker: 'Selected placements',
    featuredTitle: 'Featured AI tools',
    featuredDescription: 'Open a concise product summary, then visit the official website directly.',
    toolsSnapshot: '{count} featured tools',
    categoriesTitle: 'Browse AI tools by category',
    categoryShortcuts: '{visible} shortcuts from {total} available categories',
    allCategories: 'All categories',
    latestKicker: 'Recently added',
    latestTitle: 'Latest AI tools',
    latestDescription: 'Review newly listed products here, or open the complete paginated directory to explore every tool.',
    browseAllTools: 'Browse all AI tools',
    all: 'All',
    toolsShown: '{visible} of {total} tools shown',
    noToolsTitle: 'No tools match this filter',
    noToolsDescription: 'Choose another category to return to the latest products.',
    clearFilters: 'Show all tools',
    productOverview: 'Product overview',
    viewDetails: 'View details',
    skillsDescription: 'Reusable workflows for Codex, Claude, and other AI agents.',
    browseSkills: 'Browse all Agent Skills',
    newsTitle: 'Latest AI News',
    newsDescription: 'Current product, industry, research and funding stories.',
    browseNews: 'Browse all AI News',
    faqDescription: 'Direct answers about how AIToolly organizes products, handles pricing types and accepts new submissions.',
    submitTool: 'Submit a tool',
    openNavigation: 'Open navigation',
    closeNavigation: 'Close navigation',
    language: 'Language',
  },
  zh: {
    indexLabel: 'AIToolly 工具索引',
    searchDirectory: '搜索工具目录',
    pressSlash: '按 / 键',
    searchLabel: '你希望 AI 工具帮助完成什么？',
    quickSearches: '快速搜索',
    categoryRoutes: '{count} 个分类入口',
    latestToolsCount: '{count} 个最新收录工具',
    updated: '更新于 {date}',
    featuredKicker: '精选推荐',
    featuredTitle: 'Featured AI 工具',
    featuredDescription: '先阅读简明产品介绍，再直接访问产品官方网站。',
    toolsSnapshot: '{count} 个精选工具',
    categoriesTitle: '按分类浏览 AI 工具',
    categoryShortcuts: '展示 {visible} 个入口，共 {total} 个分类',
    allCategories: '全部分类',
    latestKicker: '最近收录',
    latestTitle: '最新 AI 工具',
    latestDescription: '浏览最新收录的产品，或进入带分页的完整目录查看全部工具。',
    browseAllTools: '浏览全部 AI 工具',
    all: '全部',
    toolsShown: '显示 {visible} 个，共 {total} 个工具',
    noToolsTitle: '当前筛选没有匹配工具',
    noToolsDescription: '请选择其他分类，返回最新产品列表。',
    clearFilters: '显示全部工具',
    productOverview: '产品概览',
    viewDetails: '查看详情',
    skillsDescription: '适用于 Codex、Claude 和其他 AI Agent 的可复用工作流。',
    browseSkills: '浏览全部 Agent Skills',
    newsTitle: '最新 AI 新闻',
    newsDescription: '聚合最新产品、行业、研究和融资动态。',
    browseNews: '浏览全部 AI 新闻',
    faqDescription: '了解 AIToolly 如何整理产品、展示定价类型并审核新工具提交。',
    submitTool: '提交 AI 工具',
    openNavigation: '打开导航',
    closeNavigation: '关闭导航',
    language: '语言',
  },
  ja: {
    indexLabel: 'AIToolly ツール索引',
    searchDirectory: 'ディレクトリを検索',
    pressSlash: '/ キー',
    searchLabel: 'AI ツールに何を手伝ってほしいですか？',
    quickSearches: 'クイック検索',
    categoryRoutes: '{count} 件のカテゴリー入口',
    latestToolsCount: '新着ツール {count} 件',
    updated: '{date} 更新',
    featuredKicker: '注目の掲載',
    featuredTitle: '注目の AI ツール',
    featuredDescription: '簡潔な製品概要を確認してから、公式サイトへ直接アクセスできます。',
    toolsSnapshot: '注目ツール {count} 件',
    categoriesTitle: 'カテゴリーから AI ツールを探す',
    categoryShortcuts: '全 {total} カテゴリー中 {visible} 件を表示',
    allCategories: 'すべてのカテゴリー',
    latestKicker: '最近追加',
    latestTitle: '最新の AI ツール',
    latestDescription: '新しく追加された製品を確認し、完全なディレクトリですべてのツールを探せます。',
    browseAllTools: 'すべての AI ツールを見る',
    all: 'すべて',
    toolsShown: '{total} 件中 {visible} 件を表示',
    noToolsTitle: 'この条件に一致するツールはありません',
    noToolsDescription: '別のカテゴリーを選択してください。',
    clearFilters: 'すべて表示',
    productOverview: '製品概要',
    viewDetails: '詳細を見る',
    skillsDescription: 'Codex、Claude、その他の AI エージェント向け再利用可能なワークフロー。',
    browseSkills: 'すべての Agent Skills を見る',
    newsTitle: '最新の AI ニュース',
    newsDescription: '製品、業界、研究、資金調達の最新情報。',
    browseNews: 'すべての AI ニュースを見る',
    faqDescription: 'AIToolly の製品整理、料金表示、ツール申請について回答します。',
    submitTool: 'AI ツールを掲載',
    openNavigation: 'ナビゲーションを開く',
    closeNavigation: 'ナビゲーションを閉じる',
    language: '言語',
  },
  ko: {
    indexLabel: 'AIToolly 도구 색인',
    searchDirectory: '디렉터리 검색',
    pressSlash: '/ 키',
    searchLabel: 'AI 도구가 어떤 일을 도와주길 원하시나요?',
    quickSearches: '빠른 검색',
    categoryRoutes: '카테고리 경로 {count}개',
    latestToolsCount: '최근 등록 도구 {count}개',
    updated: '{date} 업데이트',
    featuredKicker: '추천 게재',
    featuredTitle: '추천 AI 도구',
    featuredDescription: '간결한 제품 소개를 확인한 뒤 공식 웹사이트로 바로 이동하세요.',
    toolsSnapshot: '추천 도구 {count}개',
    categoriesTitle: '카테고리별 AI 도구 탐색',
    categoryShortcuts: '전체 {total}개 중 {visible}개 카테고리 바로가기',
    allCategories: '모든 카테고리',
    latestKicker: '최근 추가',
    latestTitle: '최신 AI 도구',
    latestDescription: '새로 등록된 제품을 살펴보거나 전체 디렉터리에서 모든 도구를 확인하세요.',
    browseAllTools: '모든 AI 도구 보기',
    all: '전체',
    toolsShown: '전체 {total}개 중 {visible}개 표시',
    noToolsTitle: '이 필터와 일치하는 도구가 없습니다',
    noToolsDescription: '다른 카테고리를 선택해 주세요.',
    clearFilters: '모든 도구 표시',
    productOverview: '제품 개요',
    viewDetails: '상세 보기',
    skillsDescription: 'Codex, Claude 및 기타 AI 에이전트를 위한 재사용 가능한 워크플로입니다.',
    browseSkills: '모든 Agent Skills 보기',
    newsTitle: '최신 AI 뉴스',
    newsDescription: '제품, 산업, 연구 및 투자 관련 최신 소식입니다.',
    browseNews: '모든 AI 뉴스 보기',
    faqDescription: 'AIToolly의 제품 분류, 가격 유형 표시 및 신규 도구 제출 방식을 확인하세요.',
    submitTool: 'AI 도구 제출',
    openNavigation: '탐색 열기',
    closeNavigation: '탐색 닫기',
    language: '언어',
  },
  de: {
    indexLabel: 'AIToolly Tool-Index',
    searchDirectory: 'Verzeichnis durchsuchen',
    pressSlash: 'Taste /',
    searchLabel: 'Wobei soll ein KI-Tool helfen?',
    quickSearches: 'Schnellsuche',
    categoryRoutes: '{count} Kategorie-Einstiege',
    latestToolsCount: '{count} neu gelistete Tools',
    updated: 'Aktualisiert am {date}',
    featuredKicker: 'Ausgewählte Platzierungen',
    featuredTitle: 'Empfohlene KI-Tools',
    featuredDescription: 'Lesen Sie eine kurze Produktübersicht und öffnen Sie danach direkt die offizielle Website.',
    toolsSnapshot: '{count} empfohlene Tools',
    categoriesTitle: 'KI-Tools nach Kategorie durchsuchen',
    categoryShortcuts: '{visible} von {total} Kategorien',
    allCategories: 'Alle Kategorien',
    latestKicker: 'Neu hinzugefügt',
    latestTitle: 'Neueste KI-Tools',
    latestDescription: 'Prüfen Sie neue Produkte oder öffnen Sie das vollständige Verzeichnis mit Seitennavigation.',
    browseAllTools: 'Alle KI-Tools ansehen',
    all: 'Alle',
    toolsShown: '{visible} von {total} Tools angezeigt',
    noToolsTitle: 'Keine Tools für diesen Filter',
    noToolsDescription: 'Wählen Sie eine andere Kategorie.',
    clearFilters: 'Alle Tools anzeigen',
    productOverview: 'Produktübersicht',
    viewDetails: 'Details ansehen',
    skillsDescription: 'Wiederverwendbare Workflows für Codex, Claude und andere KI-Agenten.',
    browseSkills: 'Alle Agent Skills ansehen',
    newsTitle: 'Aktuelle KI-News',
    newsDescription: 'Neuigkeiten zu Produkten, Branche, Forschung und Finanzierung.',
    browseNews: 'Alle KI-News ansehen',
    faqDescription: 'Antworten zur Kategorisierung, Preisangaben und Einreichung neuer Tools bei AIToolly.',
    submitTool: 'KI-Tool einreichen',
    openNavigation: 'Navigation öffnen',
    closeNavigation: 'Navigation schließen',
    language: 'Sprache',
  },
  es: {
    indexLabel: 'Índice de herramientas AIToolly',
    searchDirectory: 'Buscar en el directorio',
    pressSlash: 'Pulsa /',
    searchLabel: '¿En qué quieres que te ayude una herramienta de IA?',
    quickSearches: 'Búsquedas rápidas',
    categoryRoutes: '{count} accesos por categoría',
    latestToolsCount: '{count} herramientas recién añadidas',
    updated: 'Actualizado el {date}',
    featuredKicker: 'Selección destacada',
    featuredTitle: 'Herramientas de IA destacadas',
    featuredDescription: 'Consulta un resumen del producto y visita directamente su sitio oficial.',
    toolsSnapshot: '{count} herramientas destacadas',
    categoriesTitle: 'Explorar herramientas de IA por categoría',
    categoryShortcuts: '{visible} accesos de {total} categorías',
    allCategories: 'Todas las categorías',
    latestKicker: 'Añadidas recientemente',
    latestTitle: 'Últimas herramientas de IA',
    latestDescription: 'Revisa los productos recién añadidos o abre el directorio completo con paginación.',
    browseAllTools: 'Ver todas las herramientas de IA',
    all: 'Todas',
    toolsShown: 'Se muestran {visible} de {total} herramientas',
    noToolsTitle: 'No hay herramientas para este filtro',
    noToolsDescription: 'Elige otra categoría para volver a los productos recientes.',
    clearFilters: 'Mostrar todas',
    productOverview: 'Resumen del producto',
    viewDetails: 'Ver detalles',
    skillsDescription: 'Flujos reutilizables para Codex, Claude y otros agentes de IA.',
    browseSkills: 'Ver todas las Agent Skills',
    newsTitle: 'Últimas noticias de IA',
    newsDescription: 'Actualidad de productos, industria, investigación y financiación.',
    browseNews: 'Ver todas las noticias de IA',
    faqDescription: 'Respuestas sobre categorías, precios y envío de nuevas herramientas en AIToolly.',
    submitTool: 'Enviar una herramienta',
    openNavigation: 'Abrir navegación',
    closeNavigation: 'Cerrar navegación',
    language: 'Idioma',
  },
  fr: {
    indexLabel: 'Index des outils AIToolly',
    searchDirectory: 'Rechercher dans l’annuaire',
    pressSlash: 'Touche /',
    searchLabel: 'Que souhaitez-vous accomplir avec un outil d’IA ?',
    quickSearches: 'Recherches rapides',
    categoryRoutes: '{count} accès par catégorie',
    latestToolsCount: '{count} outils ajoutés récemment',
    updated: 'Mis à jour le {date}',
    featuredKicker: 'Sélection mise en avant',
    featuredTitle: 'Outils d’IA à la une',
    featuredDescription: 'Consultez un résumé du produit, puis accédez directement à son site officiel.',
    toolsSnapshot: '{count} outils à la une',
    categoriesTitle: 'Parcourir les outils d’IA par catégorie',
    categoryShortcuts: '{visible} raccourcis parmi {total} catégories',
    allCategories: 'Toutes les catégories',
    latestKicker: 'Ajouts récents',
    latestTitle: 'Derniers outils d’IA',
    latestDescription: 'Découvrez les nouveaux produits ou ouvrez l’annuaire complet avec pagination.',
    browseAllTools: 'Voir tous les outils d’IA',
    all: 'Tous',
    toolsShown: '{visible} outils affichés sur {total}',
    noToolsTitle: 'Aucun outil pour ce filtre',
    noToolsDescription: 'Choisissez une autre catégorie.',
    clearFilters: 'Afficher tous les outils',
    productOverview: 'Présentation du produit',
    viewDetails: 'Voir les détails',
    skillsDescription: 'Des workflows réutilisables pour Codex, Claude et d’autres agents IA.',
    browseSkills: 'Voir toutes les Agent Skills',
    newsTitle: 'Dernières actualités IA',
    newsDescription: 'Actualités produits, secteur, recherche et financement.',
    browseNews: 'Voir toutes les actualités IA',
    faqDescription: 'Réponses sur les catégories, les prix et la soumission de nouveaux outils à AIToolly.',
    submitTool: 'Soumettre un outil',
    openNavigation: 'Ouvrir la navigation',
    closeNavigation: 'Fermer la navigation',
    language: 'Langue',
  },
  pl: {
    indexLabel: 'Indeks narzędzi AIToolly',
    searchDirectory: 'Przeszukaj katalog',
    pressSlash: 'Naciśnij /',
    searchLabel: 'W czym ma pomóc narzędzie AI?',
    quickSearches: 'Szybkie wyszukiwanie',
    categoryRoutes: '{count} wejść do kategorii',
    latestToolsCount: '{count} ostatnio dodanych narzędzi',
    updated: 'Aktualizacja: {date}',
    featuredKicker: 'Wybrane pozycje',
    featuredTitle: 'Polecane narzędzia AI',
    featuredDescription: 'Przeczytaj krótki opis produktu i przejdź bezpośrednio do oficjalnej strony.',
    toolsSnapshot: '{count} polecanych narzędzi',
    categoriesTitle: 'Przeglądaj narzędzia AI według kategorii',
    categoryShortcuts: '{visible} skrótów z {total} kategorii',
    allCategories: 'Wszystkie kategorie',
    latestKicker: 'Ostatnio dodane',
    latestTitle: 'Najnowsze narzędzia AI',
    latestDescription: 'Sprawdź nowe produkty lub otwórz pełny katalog z paginacją.',
    browseAllTools: 'Zobacz wszystkie narzędzia AI',
    all: 'Wszystkie',
    toolsShown: 'Wyświetlono {visible} z {total} narzędzi',
    noToolsTitle: 'Brak narzędzi dla tego filtra',
    noToolsDescription: 'Wybierz inną kategorię.',
    clearFilters: 'Pokaż wszystkie',
    productOverview: 'Opis produktu',
    viewDetails: 'Zobacz szczegóły',
    skillsDescription: 'Wielokrotnego użytku procesy dla Codex, Claude i innych agentów AI.',
    browseSkills: 'Zobacz wszystkie Agent Skills',
    newsTitle: 'Najnowsze wiadomości AI',
    newsDescription: 'Aktualności o produktach, branży, badaniach i finansowaniu.',
    browseNews: 'Zobacz wszystkie wiadomości AI',
    faqDescription: 'Odpowiedzi dotyczące kategorii, cen i zgłaszania nowych narzędzi w AIToolly.',
    submitTool: 'Zgłoś narzędzie',
    openNavigation: 'Otwórz nawigację',
    closeNavigation: 'Zamknij nawigację',
    language: 'Język',
  },
  nl: {
    indexLabel: 'AIToolly toolindex',
    searchDirectory: 'Doorzoek de directory',
    pressSlash: 'Druk op /',
    searchLabel: 'Waarmee moet een AI-tool je helpen?',
    quickSearches: 'Snel zoeken',
    categoryRoutes: '{count} categorie-ingangen',
    latestToolsCount: '{count} recent toegevoegde tools',
    updated: 'Bijgewerkt op {date}',
    featuredKicker: 'Uitgelichte plaatsingen',
    featuredTitle: 'Uitgelichte AI-tools',
    featuredDescription: 'Lees een korte productbeschrijving en bezoek daarna direct de officiële website.',
    toolsSnapshot: '{count} uitgelichte tools',
    categoriesTitle: 'Bekijk AI-tools per categorie',
    categoryShortcuts: '{visible} snelkoppelingen uit {total} categorieën',
    allCategories: 'Alle categorieën',
    latestKicker: 'Recent toegevoegd',
    latestTitle: 'Nieuwste AI-tools',
    latestDescription: 'Bekijk nieuwe producten of open de volledige directory met paginering.',
    browseAllTools: 'Bekijk alle AI-tools',
    all: 'Alle',
    toolsShown: '{visible} van {total} tools weergegeven',
    noToolsTitle: 'Geen tools voor dit filter',
    noToolsDescription: 'Kies een andere categorie.',
    clearFilters: 'Alle tools tonen',
    productOverview: 'Productoverzicht',
    viewDetails: 'Details bekijken',
    skillsDescription: 'Herbruikbare workflows voor Codex, Claude en andere AI-agents.',
    browseSkills: 'Bekijk alle Agent Skills',
    newsTitle: 'Laatste AI-nieuws',
    newsDescription: 'Nieuws over producten, sector, onderzoek en financiering.',
    browseNews: 'Bekijk al het AI-nieuws',
    faqDescription: 'Antwoorden over categorieën, prijzen en het indienen van nieuwe tools bij AIToolly.',
    submitTool: 'Tool indienen',
    openNavigation: 'Navigatie openen',
    closeNavigation: 'Navigatie sluiten',
    language: 'Taal',
  },
  ru: {
    indexLabel: 'Каталог инструментов AIToolly',
    searchDirectory: 'Поиск по каталогу',
    pressSlash: 'Нажмите /',
    searchLabel: 'Какую задачу должен решить инструмент ИИ?',
    quickSearches: 'Быстрый поиск',
    categoryRoutes: '{count} входов в категории',
    latestToolsCount: '{count} недавно добавленных инструментов',
    updated: 'Обновлено {date}',
    featuredKicker: 'Избранные размещения',
    featuredTitle: 'Рекомендуемые ИИ-инструменты',
    featuredDescription: 'Прочитайте краткий обзор продукта и перейдите на официальный сайт.',
    toolsSnapshot: '{count} рекомендуемых инструментов',
    categoriesTitle: 'ИИ-инструменты по категориям',
    categoryShortcuts: '{visible} ссылок из {total} категорий',
    allCategories: 'Все категории',
    latestKicker: 'Недавно добавлены',
    latestTitle: 'Новые ИИ-инструменты',
    latestDescription: 'Изучите новые продукты или откройте полный каталог с постраничной навигацией.',
    browseAllTools: 'Смотреть все ИИ-инструменты',
    all: 'Все',
    toolsShown: 'Показано {visible} из {total} инструментов',
    noToolsTitle: 'Для этого фильтра нет инструментов',
    noToolsDescription: 'Выберите другую категорию.',
    clearFilters: 'Показать все',
    productOverview: 'Обзор продукта',
    viewDetails: 'Подробнее',
    skillsDescription: 'Готовые рабочие процессы для Codex, Claude и других ИИ-агентов.',
    browseSkills: 'Смотреть все Agent Skills',
    newsTitle: 'Последние новости ИИ',
    newsDescription: 'Новости продуктов, отрасли, исследований и финансирования.',
    browseNews: 'Смотреть все новости ИИ',
    faqDescription: 'Ответы о категориях, ценах и добавлении новых инструментов в AIToolly.',
    submitTool: 'Добавить инструмент',
    openNavigation: 'Открыть навигацию',
    closeNavigation: 'Закрыть навигацию',
    language: 'Язык',
  },
};

export function getHomepageEditorialCopy(locale: string): HomepageEditorialCopy {
  return COPY[locale] || COPY.en;
}

export function formatHomepageCopy(template: string, values: Record<string, string | number>): string {
  return Object.entries(values).reduce(
    (result, [key, value]) => result.replaceAll(`{${key}}`, String(value)),
    template,
  );
}

export function homepagePath(locale: string, pathname = ''): string {
  const normalized = pathname === '/' ? '' : pathname;
  if (locale === 'en') return normalized || '/';
  return `/${locale}${normalized}`;
}
